import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { LayoutDashboard, Settings, BarChart3, Wallet, LogOut, Menu, X, ChevronRight, FileText, AlertTriangle, Star, Heart, Bell } from 'lucide-react';
import { useLang } from '@/lib/i18n';
import { useSettings } from '@/lib/settingsContext';
import { useCurrency } from '@/lib/currencyContext';
import { toast } from 'sonner';
import BlockedScreen from '@/components/BlockedScreen';
import PushSubscribeButton from '@/components/PushSubscribeButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CabinetLayout() {
  const [user, setUser] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const siteSettings = useSettings();
  const { formatPrice } = useCurrency();

  // Nav items differ based on role
  const clientNav = [
    { to: '/konto', icon: LayoutDashboard, label: t('cabinet.dashboard'), exact: true },
    { to: '/konto/favorites', icon: Heart, label: t('cabinet.favorites') },
    { to: '/konto/subscriptions', icon: Bell, label: t('cabinet.subscriptions') },
    { to: '/konto/balans', icon: Wallet, label: t('cabinet.balance') },
    { to: '/konto/ustawienia', icon: Settings, label: t('cabinet.settings') },
  ];

  const modelNav = [
    { to: '/konto', icon: LayoutDashboard, label: t('cabinet.dashboard'), exact: true },
    { to: '/konto/model-profile', icon: Star, label: t('cabinet.modelProfile') },
    { to: '/konto/ankiety', icon: FileText, label: t('cabinet.applications') },
    { to: '/konto/statystyki', icon: BarChart3, label: t('cabinet.stats') },
    { to: '/konto/balans', icon: Wallet, label: t('cabinet.balance') },
    { to: '/konto/ustawienia', icon: Settings, label: t('cabinet.settings') },
  ];

  const isModel = user?.role === 'model';
  const allNavItems = isModel ? modelNav : clientNav;

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    const data = localStorage.getItem('user_data');
    if (!token || !data) { navigate('/logowanie'); return; }
    setUser(JSON.parse(data));
    // Refresh user from server so newly-blocked accounts get the lockout screen
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(fresh => {
        if (fresh) {
          setUser(fresh);
          localStorage.setItem('user_data', JSON.stringify(fresh));
        }
      })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => { setMobileMenu(false); }, [location]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'user_token' && !e.newValue) window.location.href = '/';
      if (e.key === 'user_token' && e.newValue) window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const logout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    window.location.href = '/';
  };

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const handleResendVerification = async () => {
    try {
      const token = localStorage.getItem('user_token');
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) toast.success(t('cabinet.verificationSent'));
      else toast.error(t('common.error'));
    } catch { toast.error(t('common.error')); }
  };

  if (!user) return null;

  // Hard lockout for blocked accounts — replaces the entire cabinet UI
  if (user.isBlocked) return <BlockedScreen user={user} />;

  return (
    <div className="min-h-screen">
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[var(--color-surface)]/80 backdrop-blur-lg sticky top-16 z-20">
        <span className="text-sm text-white font-medium">{user.username}</span>
        <button onClick={() => setMobileMenu(!mobileMenu)} className="text-white/60">
          {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <aside className="hidden md:block w-64 shrink-0">
          <div className="bg-[var(--color-surface)]/60 backdrop-blur-xl rounded-2xl border border-white/10 p-4 sticky top-24">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
              <div className="w-10 h-10 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center text-[var(--color-primary)] font-bold text-sm">
                {user.username?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.username}</p>
                <p className="text-xs text-white/40 truncate">{user.email}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {allNavItems.map(item => (
                <Link key={item.to} to={item.to} data-testid={`cabinet-nav-${item.to.split('/').pop() || 'main'}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive(item)
                    ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                  <item.icon className="w-4 h-4 shrink-0" />{item.label}
                </Link>
              ))}
              <button onClick={logout} data-testid="cabinet-logout"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-400/70 hover:text-red-400 hover:bg-red-500/5 w-full transition-colors">
                <LogOut className="w-4 h-4" /> {t('nav.logout')}
              </button>
            </nav>
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/30">{t('cabinet.balance')}</p>
                  <p className="text-lg font-bold text-[var(--color-primary)]">{formatPrice(user.balance || 0)}</p>
                </div>
                <Link to="/konto/balans" data-testid="balance-topup-btn"
                  className="no-underline w-8 h-8 rounded-full bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 flex items-center justify-center text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20 transition-colors">
                  <span className="text-lg leading-none no-underline">+</span>
                </Link>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <PushSubscribeButton mode="user" />
            </div>
          </div>
        </aside>

        {mobileMenu && (
          <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setMobileMenu(false)}>
            <div className="absolute top-28 left-4 right-4 bg-[var(--color-surface)] rounded-2xl border border-white/10 p-4" onClick={e => e.stopPropagation()}>
              <nav className="space-y-1">
                {allNavItems.map(item => (
                  <Link key={item.to} to={item.to} onClick={() => setMobileMenu(false)}
                    className={`flex items-center justify-between px-3 py-3 rounded-xl text-sm ${isActive(item)
                      ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]' : 'text-white/60'}`}>
                    <span className="flex items-center gap-3"><item.icon className="w-4 h-4" />{item.label}</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                ))}
                <button onClick={logout} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-red-400 w-full">
                  <LogOut className="w-4 h-4" /> {t('nav.logout')}
                </button>
              </nav>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0">
          {user && !user.emailVerified && (
            <div data-testid="email-not-verified-banner" className="mb-4 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl px-6 py-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-400 mb-1">{t('cabinet.emailNotVerified')}</p>
                  <p className="text-xs text-white/40 mb-3">{t('cabinet.emailVerifyHint')}</p>
                  <button onClick={handleResendVerification} data-testid="resend-verification-btn"
                    className="text-xs text-[var(--color-primary)] hover:underline font-medium">
                    {t('cabinet.resendVerification')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {user && !user.emailVerified && location.pathname !== '/konto/ustawienia' ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-yellow-400" />
              </div>
              <h2 className="text-lg font-bold text-white mb-2">{t('cabinet.accessRestricted')}</h2>
              <p className="text-sm text-white/40 mb-4">{t('cabinet.verifyToAccess')}</p>
              <Link to="/konto/ustawienia" className="text-[var(--color-primary)] hover:underline text-sm">{t('cabinet.goToSettings')}</Link>
            </div>
          ) : (
            <Outlet context={{ user, setUser }} />
          )}
        </main>
      </div>
    </div>
  );
}
